import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { 
  CheckCircle2, Send, Clock, AlertTriangle, Copy, ExternalLink, 
  RotateCcw, Filter, Building2, FileCheck, Archive,
  CheckCheck, X
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { useTasks } from '@/contexts/TaskContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientDeliverables = () => {
  // Keep this page's own approved-task fetchData() but re-sync the global store
  // after mutations so other views stay consistent.
  const { refreshTasks } = useTasks();
  const [tasks, setTasks] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountFilter, setAccountFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Dialog states
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [tasksDialogOpen, setTasksDialogOpen] = useState(false);
  const [reminderDialogOpen, setReminderDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [dialogTasksTitle, setDialogTasksTitle] = useState('');
  const [dialogTasksList, setDialogTasksList] = useState([]);
  
  // Form states
  const [sendNote, setSendNote] = useState('');
  const [revisionComment, setRevisionComment] = useState('');
  const [revisionDueDate, setRevisionDueDate] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tasksRes, accountsRes, membersRes] = await Promise.all([
        axios.get(`${API}/tasks`),
        axios.get(`${API}/accounts`),
        axios.get(`${API}/team-members`)
      ]);
      
      // Filter for approved tasks (including archived ones)
      const approvedTasks = tasksRes.data.filter(t => 
        t.approvalStatus === 'approved' && t.status === 'Done'
      );
      
      setTasks(approvedTasks);
      setAccounts(accountsRes.data);
      setTeamMembers(membersRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load deliverables');
      setLoading(false);
    }
  };

  const getMemberName = (id) => {
    const member = teamMembers.find(m => m.id === id);
    return member?.name || 'Unknown';
  };

  const getAccountName = (id) => {
    const account = accounts.find(a => a.id === id);
    return account?.name || 'Unknown';
  };

  const getAccountColor = (id) => {
    const account = accounts.find(a => a.id === id);
    return account?.color || '#4F46E5';
  };

  // Handle sending to client
  const openSendDialog = (task) => {
    setSelectedTask(task);
    setSendNote('');
    setSendDialogOpen(true);
  };

  const handleSendToClient = async () => {
    if (!selectedTask) return;
    
    const taskToRemind = { ...selectedTask };
    
    try {
      await axios.put(`${API}/tasks/${selectedTask.id}`, {
        clientStatus: 'sent_to_client',
        sentToClientAt: new Date().toISOString(),
        sentToClientNote: sendNote
      });
      
      toast.success('Marked as sent to client');
      setSendDialogOpen(false);
      
      // Update task with new sent info for reminder dialog
      taskToRemind.clientStatus = 'sent_to_client';
      taskToRemind.sentToClientAt = new Date().toISOString();
      taskToRemind.sentToClientNote = sendNote;
      
      // Show the reminder popup immediately after marking as sent
      setSelectedTask(taskToRemind);
      setReminderDialogOpen(true);
      
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update status');
    }
  };

  // Handle revision request
  const openRevisionDialog = (task) => {
    setSelectedTask(task);
    setRevisionComment('');
    setRevisionDueDate('');
    setRevisionDialogOpen(true);
  };

  const handleRequestRevision = async () => {
    if (!selectedTask || !revisionComment) {
      toast.error('Please provide revision details');
      return;
    }
    
    try {
      await axios.put(`${API}/tasks/${selectedTask.id}`, {
        clientStatus: 'client_revision_requested',
        clientRevisionComment: revisionComment,
        clientRevisionDueDate: revisionDueDate || null,
        status: 'In Progress',
        approvalStatus: null
      });
      
      toast.success('Revision requested - task reassigned to responsible person');
      setRevisionDialogOpen(false);
      setSelectedTask(null);
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error requesting revision:', error);
      toast.error('Failed to request revision');
    }
  };

  // Handle client approved
  const handleClientApproved = async (task) => {
    try {
      await axios.put(`${API}/tasks/${task.id}`, {
        clientStatus: 'client_approved',
        clientApprovedAt: new Date().toISOString()
      });
      
      toast.success('Deliverable marked as client approved!');
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update status');
    }
  };

  // Handle GLOBAL archive (Admin from Client Deliverables - archives everywhere)
  const handleArchive = async (task) => {
    try {
      await axios.put(`${API}/tasks/${task.id}`, {
        archived: true,
        archivedGlobally: true,
        archivedDate: new Date().toISOString()
      });
      toast.success('Task archived globally (removed from all views)');
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error archiving task:', error);
      toast.error('Failed to archive task');
    }
  };

  // Handle GLOBAL unarchive
  const handleUnarchive = async (task) => {
    try {
      await axios.put(`${API}/tasks/${task.id}`, {
        archived: false,
        archivedGlobally: false,
        archivedDate: null
      });
      toast.success('Task restored globally');
      fetchData();
      refreshTasks();
    } catch (error) {
      console.error('Error restoring task:', error);
      toast.error('Failed to restore task');
    }
  };

  // Copy link to clipboard
  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    toast.success('Link copied to clipboard');
  };

  // Get filtered tasks
  const getFilteredTasks = () => {
    let filtered = tasks;
    
    if (accountFilter !== 'all') {
      filtered = filtered.filter(t => t.accountId === accountFilter);
    }
    
    if (statusFilter !== 'all') {
      if (statusFilter === 'pending') {
        filtered = filtered.filter(t => !t.clientStatus);
      } else if (statusFilter === 'archived') {
        filtered = filtered.filter(t => t.archived || t.archivedGlobally);
      } else {
        filtered = filtered.filter(t => t.clientStatus === statusFilter);
      }
    }
    
    // Sort by date (newest first)
    return [...filtered].sort((a, b) => {
      const dateA = a.approvalDate ? new Date(a.approvalDate) : new Date(0);
      const dateB = b.approvalDate ? new Date(b.approvalDate) : new Date(0);
      return dateB - dateA;
    });
  };

  // Status badge component
  const getStatusBadge = (task) => {
    if (task.archived || task.archivedGlobally) {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
          <Archive className="h-3 w-3" />
          Archived
        </span>
      );
    }
    if (task.clientStatus === 'client_approved') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
          <CheckCheck className="h-3 w-3" />
          Client Approved
        </span>
      );
    }
    if (task.clientStatus === 'sent_to_client') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
          <Send className="h-3 w-3" />
          Sent to Client
        </span>
      );
    }
    if (task.clientStatus === 'client_revision_requested') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
          <RotateCcw className="h-3 w-3" />
          Client Revision
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium">
        <FileCheck className="h-3 w-3" />
        Ready to Send
      </span>
    );
  };

  // Stats with task lists
  const getTasksByStatus = (status) => {
    switch (status) {
      case 'total':
        return tasks;
      case 'readyToSend':
        return tasks.filter(t => !t.clientStatus && !t.archived && !t.archivedGlobally);
      case 'sentToClient':
        return tasks.filter(t => t.clientStatus === 'sent_to_client');
      case 'clientApproved':
        return tasks.filter(t => t.clientStatus === 'client_approved');
      case 'revisionRequested':
        return tasks.filter(t => t.clientStatus === 'client_revision_requested');
      default:
        return [];
    }
  };

  const stats = {
    total: tasks.length,
    readyToSend: tasks.filter(t => !t.clientStatus && !t.archived && !t.archivedGlobally).length,
    sentToClient: tasks.filter(t => t.clientStatus === 'sent_to_client').length,
    clientApproved: tasks.filter(t => t.clientStatus === 'client_approved').length,
    revisionRequested: tasks.filter(t => t.clientStatus === 'client_revision_requested').length
  };

  // Handle stat card click
  const handleStatClick = (statType, title) => {
    const tasksList = getTasksByStatus(statType);
    setDialogTasksTitle(title);
    setDialogTasksList(tasksList);
    setTasksDialogOpen(true);
  };

  // Render task card (used in both main view and dialog) - COMPACT VERSION
  const renderTaskCard = (task, isInDialog = false) => (
    <div 
      key={task.id} 
      className="flex items-center justify-between p-3 bg-white border rounded-lg hover:shadow-sm transition-shadow gap-3"
    >
      {/* Left: Account color + Title + Details */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span 
          className="w-2 h-2 rounded-full flex-shrink-0" 
          style={{ backgroundColor: getAccountColor(task.accountId) }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-medium text-sm text-foreground truncate">{task.title}</h3>
          </div>
          <p className="text-xs text-muted-foreground truncate">
            {getAccountName(task.accountId)} • {task.deliverable || 'No deliverable'} • Responsible: {task.responsible?.map(id => getMemberName(id)).join(', ') || 'N/A'}
          </p>
          {/* Completion Note - inline */}
          {task.completionNote && (
            <p className="text-xs text-gray-500 truncate mt-0.5">
              Note: {task.completionNote}
            </p>
          )}
        </div>
      </div>

      {/* Middle: Links */}
      {task.completionLinks && task.completionLinks.length > 0 && (
        <div className="flex items-center gap-1 flex-shrink-0">
          {task.completionLinks.slice(0, 2).map((link, idx) => (
            <a 
              key={idx}
              href={link} 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline bg-blue-50 px-2 py-1 rounded"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-3 w-3" />
              {idx + 1}
            </a>
          ))}
          {task.completionLinks.length > 2 && (
            <span className="text-xs text-muted-foreground">+{task.completionLinks.length - 2}</span>
          )}
        </div>
      )}

      {/* Right: Status Badge + Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {getStatusBadge(task)}
        
        {!isInDialog && (
          <>
            {/* Send to Client Button */}
            {!task.clientStatus && !task.archived && !task.archivedGlobally && (
              <Button
                size="sm"
                onClick={() => openSendDialog(task)}
                className="bg-blue-600 hover:bg-blue-700 h-8 text-xs"
              >
                <Send className="h-3 w-3 mr-1" />
                Send
              </Button>
            )}
            
            {/* Mark Client Approved */}
            {task.clientStatus === 'sent_to_client' && (
              <>
                <Button
                  size="sm"
                  onClick={() => handleClientApproved(task)}
                  className="bg-green-600 hover:bg-green-700 h-8 text-xs"
                >
                  <CheckCheck className="h-3 w-3 mr-1" />
                  Approved
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openRevisionDialog(task)}
                  className="text-orange-600 border-orange-300 hover:bg-orange-50 h-8 text-xs"
                >
                  <RotateCcw className="h-3 w-3" />
                </Button>
              </>
            )}

            {/* Archive/Unarchive */}
            {!task.archived && !task.archivedGlobally && task.clientStatus === 'client_approved' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleArchive(task)}
                className="text-gray-600 border-gray-300 hover:bg-gray-50 h-8 text-xs"
              >
                <Archive className="h-3 w-3" />
              </Button>
            )}
            {(task.archived || task.archivedGlobally) && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleUnarchive(task)}
                className="text-blue-600 border-blue-300 hover:bg-blue-50 h-8 text-xs"
              >
                <RotateCcw className="h-3 w-3" />
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const filteredTasks = getFilteredTasks();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="p-6 space-y-4 overflow-x-hidden"
    >
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold text-foreground">Client Deliverables</h1>
          <p className="text-sm text-muted-foreground">
            Manage approved deliverables for client review
          </p>
        </div>
      </div>

      {/* Compact Stats Row */}
      <div className="flex flex-wrap gap-2">
        <button 
          onClick={() => handleStatClick('total', 'All Approved Deliverables')}
          className="flex items-center gap-2 px-3 py-2 bg-white border rounded-lg hover:shadow-sm transition-shadow"
        >
          <FileCheck className="h-4 w-4 text-slate-600" />
          <span className="text-lg font-bold">{stats.total}</span>
          <span className="text-xs text-muted-foreground">Total</span>
        </button>
        
        <button 
          onClick={() => handleStatClick('readyToSend', 'Ready to Send')}
          className="flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-lg hover:shadow-sm transition-shadow"
        >
          <Clock className="h-4 w-4 text-purple-600" />
          <span className="text-lg font-bold text-purple-700">{stats.readyToSend}</span>
          <span className="text-xs text-purple-600">Ready</span>
        </button>
        
        <button 
          onClick={() => handleStatClick('sentToClient', 'Sent to Client')}
          className="flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg hover:shadow-sm transition-shadow"
        >
          <Send className="h-4 w-4 text-blue-600" />
          <span className="text-lg font-bold text-blue-700">{stats.sentToClient}</span>
          <span className="text-xs text-blue-600">Sent</span>
        </button>
        
        <button 
          onClick={() => handleStatClick('clientApproved', 'Client Approved')}
          className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg hover:shadow-sm transition-shadow"
        >
          <CheckCheck className="h-4 w-4 text-green-600" />
          <span className="text-lg font-bold text-green-700">{stats.clientApproved}</span>
          <span className="text-xs text-green-600">Approved</span>
        </button>
        
        <button 
          onClick={() => handleStatClick('revisionRequested', 'Revisions Requested')}
          className="flex items-center gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-lg hover:shadow-sm transition-shadow"
        >
          <RotateCcw className="h-4 w-4 text-orange-600" />
          <span className="text-lg font-bold text-orange-700">{stats.revisionRequested}</span>
          <span className="text-xs text-orange-600">Revisions</span>
        </button>
      </div>

      {/* Compact Filters Row */}
      <div className="flex flex-wrap items-center gap-3 p-3 bg-white border rounded-lg">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-muted-foreground" />
        </div>
        
        <Select value={accountFilter} onValueChange={setAccountFilter}>
          <SelectTrigger className="w-40 h-8 bg-white text-sm" data-testid="account-filter">
            <SelectValue placeholder="All Accounts" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Accounts</SelectItem>
            {accounts.map(account => (
              <SelectItem key={account.id} value={account.id}>
                <span className="flex items-center gap-2">
                  <span 
                    className="w-2 h-2 rounded-full" 
                    style={{ backgroundColor: account.color }}
                  />
                  {account.name}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 h-8 bg-white text-sm" data-testid="status-filter">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Ready to Send</SelectItem>
            <SelectItem value="sent_to_client">Sent to Client</SelectItem>
            <SelectItem value="client_approved">Client Approved</SelectItem>
            <SelectItem value="client_revision_requested">Revision Requested</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Deliverables List - Compact */}
      <div className="space-y-2">
        {filteredTasks.length === 0 ? (
          <div className="p-8 text-center bg-white border rounded-lg">
            <FileCheck className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <h3 className="text-base font-medium text-foreground">No deliverables found</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {tasks.length === 0 
                ? "No tasks have been approved by accountable people yet"
                : "No deliverables match your current filters"
              }
            </p>
          </div>
        ) : (
          filteredTasks.map(task => renderTaskCard(task))
        )}
      </div>

      {/* Tasks by Status Dialog */}
      <Dialog open={tasksDialogOpen} onOpenChange={setTasksDialogOpen}>
        <DialogContent className="bg-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialogTasksTitle} ({dialogTasksList.length})</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {dialogTasksList.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No tasks in this category</p>
            ) : (
              dialogTasksList.map(task => renderTaskCard(task, true))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Send to Client Dialog */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Send to Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedTask && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedTask.title}</p>
                <p className="text-sm text-muted-foreground">
                  {getAccountName(selectedTask.accountId)}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="sendNote">Note (Optional)</Label>
              <Textarea
                id="sendNote"
                value={sendNote}
                onChange={(e) => setSendNote(e.target.value)}
                placeholder="e.g., Sent via email to john@client.com"
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Add details about how/where you sent this deliverable
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendToClient} className="bg-blue-600 hover:bg-blue-700">
              <Send className="h-4 w-4 mr-1" />
              Mark as Sent
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Revision Dialog */}
      <Dialog open={revisionDialogOpen} onOpenChange={setRevisionDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Request Client Revision</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedTask && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium">{selectedTask.title}</p>
                <p className="text-sm text-muted-foreground">
                  {getAccountName(selectedTask.accountId)} • 
                  Responsible: {selectedTask.responsible?.map(id => getMemberName(id)).join(', ') || 'Not assigned'}
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="revisionComment">Revision Details *</Label>
              <Textarea
                id="revisionComment"
                value={revisionComment}
                onChange={(e) => setRevisionComment(e.target.value)}
                placeholder="Describe what changes the client requested..."
                rows={4}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="revisionDueDate">New Due Date (Optional)</Label>
              <Input
                id="revisionDueDate"
                type="date"
                value={revisionDueDate}
                onChange={(e) => setRevisionDueDate(e.target.value)}
              />
            </div>
            <div className="p-3 bg-orange-50 rounded-lg text-sm text-orange-700">
              <AlertTriangle className="h-4 w-4 inline mr-1" />
              This will reset the task status and reassign it to the responsible person for revision.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevisionDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRequestRevision} 
              className="bg-orange-600 hover:bg-orange-700"
              disabled={!revisionComment}
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Request Revision
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client Follow-up Reminder Dialog */}
      <Dialog open={reminderDialogOpen} onOpenChange={setReminderDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <span className="text-2xl">📢</span> Hey there, superstar!
            </DialogTitle>
          </DialogHeader>
          <div className="py-6 text-center">
            <div className="text-5xl mb-4">🎯</div>
            <p className="text-lg text-foreground leading-relaxed">
              I know you have a lot on your plate, so this is a friendly reminder to{' '}
              <span className="font-semibold text-blue-600">update our dear client</span> on the great work you've done!
            </p>
            {selectedTask && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg text-left">
                <p className="font-medium text-blue-800">{selectedTask.title}</p>
                <p className="text-sm text-blue-600">{getAccountName(selectedTask.accountId)}</p>
                {selectedTask.sentToClientAt && (
                  <p className="text-xs text-blue-500 mt-1">
                    Sent on {format(parseISO(selectedTask.sentToClientAt), 'MMM d, yyyy h:mm a')}
                  </p>
                )}
              </div>
            )}
            <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
              <p className="text-lg font-bold text-amber-700 italic">
                "Jo Dikhta hai, Wahi Bikta hai!" 🌟
              </p>
              <p className="text-xs text-amber-600 mt-1">What is seen, is what sells!</p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => setReminderDialogOpen(false)}
              className="flex-1"
            >
              Got it! 👍
            </Button>
            <Button 
              onClick={() => {
                setReminderDialogOpen(false);
                handleClientApproved(selectedTask);
              }}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              <CheckCheck className="h-4 w-4 mr-1" />
              Client Approved!
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default ClientDeliverables;
